import { CallOutlined, EmailOutlined, LocationCityOutlined } from '@mui/icons-material'
import { Box, Typography, Grid, TextField, Button, Stack, Alert, CircularProgress, Snackbar } from '@mui/material'
import { SendIcon } from 'lucide-react';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useState, useEffect } from 'react'
import emailjs from '@emailjs/browser';
import AOS from 'aos';
import 'aos/dist/aos.css';


const initialForm = { fullName: "", email: "", subject: "", message: "" };
const initialErrors = { fullName: "", email: "", subject: "", message: "" };

function validate(fields) {
    const errs = { ...initialErrors };
    if (!fields.fullName.trim()) errs.fullName = "Full name is required.";
    if (!fields.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
        errs.email = "Enter a valid email address.";
    if (!fields.subject.trim()) errs.subject = "Subject is required.";
    if (!fields.message.trim()) errs.message = "Message is required.";
    else if (fields.message.trim().length < 10)
        errs.message = "Message must be at least 10 characters.";
    return errs;
}
function ContactUs() {

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState(initialErrors);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        // Clear error on change
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async () => {
        const errs = validate(form);
        if (Object.values(errs).some(Boolean)) {
            setErrors(errs);
            return;
        }

        setLoading(true);
        try {
            await emailjs.send(
                process.env.REACT_APP_SERVICEID,
                process.env.REACT_APP_TEMPLATEID, 
                {
                    user_name: form.fullName,
                    user_email: form.email,
                    subject: form.subject,
                    message: form.message,
                },
                process.env.REACT_APP_EMAIL
            );
            setSubmitted(true);
            setSnackOpen(true);
            setForm(initialForm);
            setErrors(initialErrors);
        } catch (err) {
            console.error('EmailJS Error:', err);
            setErrors((prev) => ({
                ...prev,
                message: 'Failed to send message. Please try again.',
            }));
        } finally {
            setLoading(false);
        }
    };
    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    backgroundColor: 'var(--primary-color)',
                    py: { xs: 7, md: 10 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""', position: 'absolute',
                        top: '-80px', right: '-80px',
                        width: '320px', height: '320px',
                        borderRadius: '50%',
                        border: '70px solid rgba(192,174,93,0.07)',
                        pointerEvents: 'none',
                    },
                    '&::after': {
                        content: '""', position: 'absolute',
                        bottom: '-90px', left: '-50px',
                        width: '280px', height: '280px',
                        borderRadius: '50%',
                        border: '55px solid rgba(192,174,93,0.05)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Typography sx={{ fontSize: { xs: '34px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>
                    CONTACT US
                </Typography>
                <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
                <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'rgba(255,255,255,0.65)', maxWidth: 560, mx: 'auto' }}>
                    Great Products Start with a Conversation — Let’s Build Yours
                </Typography>
            </Box>

            {/* Main Section */}
            <Box className='p-3' sx={{ py: 6 }}>
                <Typography data-aos="fade-up" variant='h4' textAlign="center" fontWeight="bold" my={5}>
                    Let’s Connect — We’re Here to Help
                </Typography>

                <Grid container spacing={4}>

                    {/* Map */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ borderRadius: 4, overflow: "hidden", boxShadow: 3 }}>
                            <iframe
                                src="https://www.google.com/maps?q=-33.7658736,150.9104578&z=15&output=embed"
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                loading="lazy"
                                title='contact'
                            />
                        </Box>

                        {/* Contact Info */}
                    </Grid>

                    {/* Form */}
                    <Grid className="mx-auto" size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h5" fontWeight="bold" mb={2}>
                                Send us a Message
                            </Typography>

                            {submitted && (
                                <Alert
                                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                                    severity="success"
                                    sx={{ mb: 2 }}
                                    onClose={() => setSubmitted(false)}
                                >
                                    Your message was sent successfully. We'll get back to you soon!
                                </Alert>
                            )}

                            <Stack spacing={2}>
                                <TextField
                                    label="Full Name"
                                    name="fullName"
                                    value={form.fullName}
                                    onChange={handleChange}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName}
                                    fullWidth
                                    disabled={loading}
                                />
                                <TextField
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    fullWidth
                                    disabled={loading}
                                />
                                <TextField
                                    label="Subject"
                                    name="subject"
                                    value={form.subject}
                                    onChange={handleChange}
                                    error={!!errors.subject}
                                    helperText={errors.subject}
                                    fullWidth
                                    disabled={loading}
                                />
                                <TextField
                                    label="Message"
                                    name="message"
                                    multiline
                                    rows={4}
                                    value={form.message}
                                    onChange={handleChange}
                                    error={!!errors.message}
                                    helperText={errors.message}
                                    fullWidth
                                    disabled={loading}
                                />

                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    endIcon={
                                        loading
                                            ? <CircularProgress size={18} color="inherit" />
                                            : <SendIcon />
                                    }
                                   className='fs-14  btn-outlined-second  border-20'
                                >
                                    {loading ? "Sending…" : "Send Message"}
                                </Button>
                            </Stack>
                        </Box>

                        <Snackbar
                            open={snackOpen}
                            autoHideDuration={4000}
                            onClose={() => setSnackOpen(false)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        >
                            <Alert severity="success" onClose={() => setSnackOpen(false)}>
                                Message sent successfully!
                            </Alert>
                        </Snackbar>
                    </Grid>

                </Grid>
                <Grid container spacing={4} mt={3}>
                    <Grid size={{ xs: 12, md: 'auto' }}>
                        <Typography variant="h5" fontWeight="bold">
                            Our Office
                        </Typography>

                        <Typography variant="body2" gutterBottom color="text.secondary">
                          <LocationCityOutlined sx={{ color: "var(--primary-text-color)" }}/>  14/18 Third Avenue, Blacktown
                        </Typography>

                        <Typography variant="body2" gutterBottom>
                            <EmailOutlined sx={{ color: "var(--primary-text-color)" }} />&nbsp;<a href='mailto:info@clarencehomes.com.au'>info@clarencehomes.com.au</a>
                        </Typography>
                        <Box>
                            <CallOutlined sx={{ color: "var(--primary-text-color)" }} gutterBottom /> &nbsp;
                            <Box component="a"fontSize={'14px'} href="tel:1300451780">
                            1300 451 780
                            </Box>
                        </Box>
                    </Grid>
                    {/* Opening Hours */}
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Opening Hours
                        </Typography>

                        <Stack spacing={0.5}>
                            <Typography variant="body2">
                                <strong>Mon - Fri:</strong> 9:00 am – 5:00 pm
                            </Typography>
                            <Typography variant="body2">
                                <strong>Saturday:</strong> 9:00 am – 4:00 pm
                            </Typography>
                            <Typography variant="body2">
                                <strong>Sunday:</strong> Closed
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>

            </Box>
        </>
    )
}

export default ContactUs